/**
 * Drawing the font library's families on the page.
 *
 * Each file is fetched from our API with the reader's token, kept as a local
 * copy (a blob URL), and named in an `@font-face` rule of a stylesheet this
 * owns. Not a stylesheet pointing at the API: the browser would fetch it
 * without the token. A real stylesheet rather than `FontFace` objects, so the
 * PDF and HTML exports find these faces the way they find every other one,
 * among `document.styleSheets` (see `embed.ts`), and inline them.
 *
 * A file several styles share (a variable family's one file for every
 * weight) is fetched once, by its digest.
 */

import { fontFile, googleSample, type LibraryFamily, type LibraryFontFile } from '@/api/fonts'
import { libraryFamilyId, libraryFontFamily, type TypefaceId } from '@/domain/typeface'
import { useFontLibraryStore } from '@/stores/fontLibrary'

/** A family name a stylesheet may be given: letters, figures, spaces and hyphens. */
const SAFE_NAME = /^[A-Za-z0-9][A-Za-z0-9 -]{0,79}$/
/** What a `unicode-range` may hold, since it is written into a stylesheet. */
const SAFE_RANGE = /^[Uu+0-9A-Fa-f?\-, ]{0,2000}$/

let sheet: HTMLStyleElement | null = null
const copies = new Map<string, Promise<string | null>>()
const drawn = new Set<string>()
const samples = new Map<string, Promise<string | null>>()

function stylesheet(): HTMLStyleElement {
  if (!sheet) {
    sheet = document.createElement('style')
    sheet.setAttribute('data-library-fonts', '')
    document.head.appendChild(sheet)
  }
  return sheet
}

function addRules(rules: readonly string[]): void {
  const css = rules.filter(Boolean).join('\n')
  if (css) stylesheet().append(`${css}\n`)
}

/** A face as a stylesheet rule, or nothing when a value is not one it may hold. */
export function faceRule(
  family: string,
  style: { weight: number; weightMax?: number | null; italic: boolean; unicodeRange?: string },
  url: string,
): string {
  const range = style.unicodeRange ?? ''
  if (!SAFE_NAME.test(family) || !SAFE_RANGE.test(range) || !url.startsWith('blob:')) return ''
  const clamp = (value: number): number => Math.min(900, Math.max(100, Math.round(value)))
  // A variable font's range, so the page asks it for its own bold rather than faking one.
  const weight =
    style.weightMax && style.weightMax > style.weight
      ? `${clamp(style.weight)} ${clamp(style.weightMax)}`
      : `${clamp(style.weight)}`
  return [
    '@font-face {',
    `  font-family: '${family}';`,
    `  src: url(${url}) format('woff2');`,
    `  font-weight: ${weight};`,
    `  font-style: ${style.italic ? 'italic' : 'normal'};`,
    '  font-display: swap;',
    range ? `  unicode-range: ${range};` : '',
    '}',
  ]
    .filter(Boolean)
    .join('\n')
}

function localCopy(familyId: number, file: LibraryFontFile): Promise<string | null> {
  const key = file.digest || `${familyId}/${file.id}`
  let copy = copies.get(key)
  if (!copy) {
    copy = fontFile(familyId, file.id).then(
      (blob) => URL.createObjectURL(blob),
      (error: unknown) => {
        console.warn(`[typography] font file ${familyId}/${file.id} could not be fetched`, error)
        copies.delete(key)
        return null
      },
    )
    copies.set(key, copy)
  }
  return copy
}

/**
 * A family drawn under a name: its own (`dmf-<id>`) by default, or the name a
 * document already uses for it (a stand-in for a font an imported PDF used).
 */
export async function drawLibraryFamily(
  family: LibraryFamily,
  name = libraryFontFamily(family.id),
): Promise<void> {
  const key = `${name}|${family.id}`
  if (drawn.has(key)) return
  drawn.add(key)
  const rules = await Promise.all(
    family.files.map(async (file) => {
      const url = await localCopy(family.id, file)
      return url ? faceRule(name, file, url) : ''
    }),
  )
  addRules(rules)
}

/**
 * A library typeface drawn. Fails when the library does not have it (deleted,
 * or not reachable just now), so the caller tries again next time rather than
 * taking it as drawn; the page shows the stand-in meanwhile.
 */
export async function drawLibraryTypeface(id: TypefaceId): Promise<void> {
  const familyId = libraryFamilyId(id)
  if (familyId === null) return
  const family = await useFontLibraryStore().family(familyId)
  if (!family) throw new Error(`${id} is not in the font library`)
  await drawLibraryFamily(family)
}

/**
 * A Google family shown in itself in the catalogue, before anybody adds it:
 * one file, fetched by our server. Returns the name it is drawn under.
 */
export function drawGoogleSample(family: string): Promise<string | null> {
  let drawing = samples.get(family)
  if (!drawing) {
    const name = `dmf-sample-${family.replace(/[^A-Za-z0-9]+/g, '-')}`
    drawing = googleSample(family).then(
      (blob) => {
        addRules([faceRule(name, { weight: 400, italic: false }, URL.createObjectURL(blob))])
        return name
      },
      () => {
        // Not kept: the next row to show it asks again.
        samples.delete(family)
        return null
      },
    )
    samples.set(family, drawing)
  }
  return drawing
}
